import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Save,
  X,
  Plus,
  Calendar,
  DollarSign,
  Users,
  Clock,
  FileText,
  Settings,
  Calculator,
  Activity,
  Trash2,
  Database
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import ContractService from '../../services/contractService';
import MasterDataService from '../../services/masterDataService';
import TimeMaterialsService from '../../services/timeMaterialsService';
import { useToast, ToastContainer } from '../common/Toast';
import API_CONFIG from '../../config/api';

const CreateTimeMaterialsContract = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();
  
  // Tab management
  const [activeTab, setActiveTab] = useState('basic');
  
  // Test mode state
  const [testMode, setTestMode] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    title: '',
    type: 'Time & Materials',
    counterparty: '',
    status: 'Draft',
    priority: 'medium',
    startDate: '',
    endDate: '',
    description: '',
    
    // Financial Details
    currency: 'USD',
    costCeiling: '',
    budgetAllocation: '',
    
    // Time Tracking Configuration
    timeTracking: {
      enabled: true,
      required: true,
      approvalRequired: true,
      maxHoursPerDay: 8
    },
    
    // Cost Tracking
    costTracking: {
      enabled: true,
      categories: [],
      budgetLimits: []
    },
    
    // Billing Rules
    billingRules: {
      minimumHours: 0.25,
      roundingRules: 'quarter-hour',
      travelTime: false,
      administrativeTime: true
    },
    
    // Hourly Rates
    hourlyRates: [],
    
    // Material Costs
    materialCosts: [],
    
    // Additional Details
    department: '',
    location: '',
    tags: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contractTypes, setContractTypes] = useState([]);
  const [contractStatuses, setContractStatuses] = useState([
  { label: 'Draft', value: 'DRAFT' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Expired', value: 'EXPIRED' }
]);
  const [currencies, setCurrencies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);

  // Load dropdown data and test mode
  useEffect(() => {
    loadTestMode();
    loadDropdownData();
  }, []);

  const loadTestMode = async () => {
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.SETTINGS, {
        headers: API_CONFIG.HEADERS
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          const testModeValue = result.data.testMode ?? false;
          setTestMode(testModeValue);
          console.log('Test mode loaded from API:', testModeValue);
        } else {
          // Fallback to localStorage if API response doesn't have data
          const localStorageTestMode = localStorage.getItem('testMode') === 'true';
          setTestMode(localStorageTestMode);
          console.log('Test mode loaded from localStorage:', localStorageTestMode);
        }
      } else {
        // Fallback to localStorage if API response is not ok
        const localStorageTestMode = localStorage.getItem('testMode') === 'true';
        setTestMode(localStorageTestMode);
        console.log('Test mode loaded from localStorage (API not ok):', localStorageTestMode);
      }
    } catch (error) {
      console.error('Error loading test mode setting:', error);
      // Fallback to localStorage if API fails
      const localStorageTestMode = localStorage.getItem('testMode') === 'true';
      setTestMode(localStorageTestMode);
      console.log('Test mode loaded from localStorage (API error):', localStorageTestMode);
    }
  };

  const loadDropdownData = async () => {
    try {
      const [masterDataRes, typesRes, statusesRes] = await Promise.all([
        MasterDataService.getContractDropdowns().catch(() => ({ success: false, data: {} })),
        ContractService.getContractTypes().catch(() => ({ success: false, data: [] })),
        ContractService.getContractStatuses().catch(() => ({ success: false, data: [] }))
      ]);

      const dropdownData = masterDataRes.success && masterDataRes.data ? masterDataRes.data : {};
      
      // Transform master data to dropdown format
      setContractTypes(typesRes.success && typesRes.data ? typesRes.data : 
        (dropdownData.ContractType?.map(item => ({
          value: item.code,
          label: item.label
        })) || []));
      
      setContractStatuses(statusesRes.success && statusesRes.data ? statusesRes.data :
        (dropdownData.Status?.map(item => ({
          value: item.code,
          label: item.label
        })) || []));
      
      setCurrencies(dropdownData.Currency?.map(item => ({
        value: item.code,
        label: item.label
      })) || []);
      
      setDepartments(dropdownData.Department?.map(item => ({
        value: item.code,
        label: item.label
      })) || []);
      
      setLocations(dropdownData.Location?.map(item => ({
        value: item.code,
        label: item.label
      })) || []);
    } catch (error) {
      console.error('Error loading dropdown data:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleArrayAdd = (field, newItem) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), newItem]
    }));
  };

  const handleArrayRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleArrayItemChange = (field, index, subField, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => 
        i === index ? { ...item, [subField]: value } : item
      )
    }));
  };

  const fillTestData = () => {
    const testData = {
      title: 'Software Development Services - T&M Contract',
      counterparty: 'TechStart Inc',
      status: 'Draft',
      priority: 'high',
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      description: 'Comprehensive time & materials contract for software development services including web application development, mobile app development, and cloud infrastructure setup. This contract includes hourly rates for various roles, material cost tracking for software licenses and third-party services, and detailed time tracking requirements.',
      currency: 'USD',
      costCeiling: '150000',
      budgetAllocation: 'Development: 60%, Testing: 25%, Consulting: 15%',
      timeTracking: {
        enabled: true,
        required: true,
        approvalRequired: true,
        maxHoursPerDay: 8
      },
      costTracking: {
        enabled: true,
        categories: ['Development', 'Testing', 'Consulting', 'DevOps'],
        budgetLimits: ['90000', '37500', '22500', '0']
      },
      billingRules: {
        minimumHours: 0.25,
        roundingRules: 'quarter-hour',
        travelTime: false,
        administrativeTime: true
      },
      hourlyRates: [
        { role: 'Senior Developer', rate: 150 },
        { role: 'Junior Developer', rate: 100 },
        { role: 'Project Manager', rate: 125 },
        { role: 'QA Engineer', rate: 110 },
        { role: 'DevOps Engineer', rate: 140 },
        { role: 'UI/UX Designer', rate: 120 }
      ],
      materialCosts: [
        { category: 'Software Licenses', markup: 15 },
        { category: 'Hardware', markup: 10 },
        { category: 'Third-party Services', markup: 20 },
        { category: 'Cloud Services', markup: 12 },
        { category: 'Development Tools', markup: 18 }
      ],
      department: 'IT',
      location: 'Headquarters',
      tags: ['Software Development', 'T&M', 'High Priority']
    };
    
    setFormData(prev => ({ ...prev, ...testData }));
    success('Test data filled successfully!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const costCeiling = parseFloat(formData.costCeiling) || 0;

      const contractData = {
        title: formData.title,
        type: 'Time & Materials',
        counterparty: formData.counterparty,
        status: formData.status || 'Draft',
        priority: formData.priority || 'medium',
        startDate: formData.startDate,
        endDate: formData.endDate,
        description: formData.description,
        amount: costCeiling,
        currency: formData.currency || 'USD',
        paymentTerms: 'NET_30',
        frequency: 'MONTHLY',
        department: formData.department,
        location: formData.location,
        tags: formData.tags || []
      };

      const response = await ContractService.createContract(contractData);

      if (!response.success) {
        throw new Error(response.message || 'Failed to create contract');
      }

      const createdContractId = response.data?.contractId || response.data?.id;

      if (!createdContractId) {
        throw new Error('Contract identifier is missing from the server response');
      }

      const defaultHourlyRate = formData.hourlyRates?.[0]?.rate ?? 0;
      await TimeMaterialsService.createTimeMaterials(createdContractId, {
        hourlyRates: formData.hourlyRates || [],
        defaultHourlyRate,
        laborEntries: [],
        materialEntries: [],
        status: (formData.status || 'Draft').toLowerCase()
      });

      success('Time & Materials contract created successfully!');
      navigate('/contracts/time-materials');
    } catch (error) {
      console.error('Error creating contract:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to create contract';
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Tab configuration
  const tabs = [
    {
      id: 'basic',
      label: 'Basic Information',
      icon: <FileText size={18} />
    },
    {
      id: 'rates',
      label: 'Rates & Costs',
      icon: <DollarSign size={18} />
    },
    {
      id: 'tracking',
      label: 'Time Tracking',
      icon: <Clock size={18} />
    },
    {
      id: 'billing',
      label: 'Billing Rules',
      icon: <Calculator size={18} />
    }
  ];

  const renderBasicInformation = () => (
    <div className="project-form-section">
      <div className="section-header">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <FileText size={20} style={{ color: '#4678be' }} />
          <h3 className="section-title" style={{
            fontSize: '18px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            margin: 0,
            fontFamily: 'var(--font-family)'
          }}>
            Basic Information
          </h3>
        </div>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
            marginBottom: '8px',
            fontFamily: 'var(--font-family)'
          }}>
            Contract Title *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid var(--border-color)',
              borderRadius: '0',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'white'
            }}
            placeholder="Enter contract title"
            required
          />
        </div>
        
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
            marginBottom: '8px',
            fontFamily: 'var(--font-family)'
          }}>
            Counterparty *
          </label>
          <input
            type="text"
            value={formData.counterparty}
            onChange={(e) => handleInputChange('counterparty', e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid var(--border-color)',
              borderRadius: '0',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'white'
            }}
            placeholder="Enter counterparty name"
            required
          />
        </div>
        
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
            marginBottom: '8px',
            fontFamily: 'var(--font-family)'
          }}>
            Status *
          </label>
          <select
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid var(--border-color)',
              borderRadius: '0',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'white'
            }}
            required
          >
            <option value="">Select Status</option>
            {contractStatuses.map(status => (
              <option key={status.value || status} value={status.value || status}>
                {status.label || status}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
            marginBottom: '8px',
            fontFamily: 'var(--font-family)'
          }}>
            Priority
          </label>
          <select
            value={formData.priority}
            onChange={(e) => handleInputChange('priority', e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid var(--border-color)',
              borderRadius: '0',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'white'
            }}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
        
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
            marginBottom: '8px',
            fontFamily: 'var(--font-family)'
          }}>
            Start Date *
          </label>
          <input
            type="date"
            value={formData.startDate}
            onChange={(e) => handleInputChange('startDate', e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid var(--border-color)',
              borderRadius: '0',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'white'
            }}
            required
          />
        </div>
        
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
            marginBottom: '8px',
            fontFamily: 'var(--font-family)'
          }}>
            End Date *
          </label>
          <input
            type="date"
            value={formData.endDate}
            onChange={(e) => handleInputChange('endDate', e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid var(--border-color)',
              borderRadius: '0',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'white'
            }}
            required
          />
        </div>
        
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
            marginBottom: '8px',
            fontFamily: 'var(--font-family)'
          }}>
            Currency *
          </label>
          <select
            value={formData.currency}
            onChange={(e) => handleInputChange('currency', e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid var(--border-color)',
              borderRadius: '0',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'white'
            }}
            required
          >
            <option value="">Select Currency</option>
            {currencies.map(currency => (
              <option key={currency.value || currency} value={currency.value || currency}>
                {currency.label || currency}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
            marginBottom: '8px',
            fontFamily: 'var(--font-family)'
          }}>
            Cost Ceiling
          </label>
          <input
            type="number"
            value={formData.costCeiling}
            onChange={(e) => handleInputChange('costCeiling', e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid var(--border-color)',
              borderRadius: '0',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'white'
            }}
            placeholder="0.00"
            step="0.01"
          />
        </div>
        
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
            marginBottom: '8px',
            fontFamily: 'var(--font-family)'
          }}>
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid var(--border-color)',
              borderRadius: '0',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'white',
              resize: 'vertical',
              minHeight: '100px'
            }}
            placeholder="Enter contract description"
          />
        </div>
      </div>
    </div>
  );

  const renderRatesAndCosts = () => (
    <div>
      <div className="project-form-section">
        <div className="section-header">
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <DollarSign size={20} style={{ color: '#4678be' }} />
            <h3 className="section-title" style={{
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              margin: 0,
              fontFamily: 'var(--font-family)'
            }}>
              Hourly Rates by Role
            </h3>
          </div>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {formData.hourlyRates.map((rate, index) => (
            <div key={index} style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr auto',
              gap: '12px',
              alignItems: 'end'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Role
                </label>
                <input
                  type="text"
                  value={rate.role || ''}
                  onChange={(e) => handleArrayItemChange('hourlyRates', index, 'role', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    backgroundColor: 'white'
                  }}
                  placeholder="e.g., Senior Developer"
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Rate (per hour)
                </label>
                <input
                  type="number"
                  value={rate.rate || ''}
                  onChange={(e) => handleArrayItemChange('hourlyRates', index, 'rate', parseFloat(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    backgroundColor: 'white'
                  }}
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              <button
                type="button"
                onClick={() => handleArrayRemove('hourlyRates', index)}
                style={{
                  padding: '12px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => handleArrayAdd('hourlyRates', { role: '', rate: 0 })}
            style={{
              padding: '12px 24px',
              backgroundColor: '#4678be',
              color: 'white',
              border: 'none',
              borderRadius: '0',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              fontFamily: 'var(--font-family)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: 'fit-content'
            }}
          >
            <Plus size={18} />
            Add Hourly Rate
          </button>
        </div>
      </div>

      <div className="project-form-section">
        <div className="section-header">
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Calculator size={20} style={{ color: '#4678be' }} />
            <h3 className="section-title" style={{
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              margin: 0,
              fontFamily: 'var(--font-family)'
            }}>
              Material Cost Categories
            </h3>
          </div>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {formData.materialCosts.map((cost, index) => (
            <div key={index} style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr auto',
              gap: '12px',
              alignItems: 'end'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Category
                </label>
                <input
                  type="text"
                  value={cost.category || ''}
                  onChange={(e) => handleArrayItemChange('materialCosts', index, 'category', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    backgroundColor: 'white'
                  }}
                  placeholder="e.g., Software Licenses"
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  marginBottom: '8px',
                  fontFamily: 'var(--font-family)'
                }}>
                  Markup (%)
                </label>
                <input
                  type="number"
                  value={cost.markup || ''}
                  onChange={(e) => handleArrayItemChange('materialCosts', index, 'markup', parseFloat(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                    backgroundColor: 'white'
                  }}
                  placeholder="0"
                  step="0.01"
                />
              </div>
              <button
                type="button"
                onClick={() => handleArrayRemove('materialCosts', index)}
                style={{
                  padding: '12px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => handleArrayAdd('materialCosts', { category: '', markup: 0 })}
            style={{
              padding: '12px 24px',
              backgroundColor: '#4678be',
              color: 'white',
              border: 'none',
              borderRadius: '0',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              fontFamily: 'var(--font-family)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: 'fit-content'
            }}
          >
            <Plus size={18} />
            Add Material Cost Category
          </button>
        </div>
      </div>
    </div>
  );

  const renderTimeTracking = () => (
    <div className="project-form-section">
      <div className="section-header">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Clock size={20} style={{ color: '#4678be' }} />
          <h3 className="section-title" style={{
            fontSize: '18px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            margin: 0,
            fontFamily: 'var(--font-family)'
          }}>
            Time Tracking Configuration
          </h3>
        </div>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            checked={formData.timeTracking.enabled}
            onChange={(e) => handleNestedInputChange('timeTracking', 'enabled', e.target.checked)}
            style={{
              width: '18px',
              height: '18px',
              cursor: 'pointer'
            }}
          />
          <label style={{
            fontSize: '14px',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            cursor: 'pointer'
          }}>
            Enable Time Tracking
          </label>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            checked={formData.timeTracking.required}
            onChange={(e) => handleNestedInputChange('timeTracking', 'required', e.target.checked)}
            style={{
              width: '18px',
              height: '18px',
              cursor: 'pointer'
            }}
          />
          <label style={{
            fontSize: '14px',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            cursor: 'pointer'
          }}>
            Time Tracking Required
          </label>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            checked={formData.timeTracking.approvalRequired}
            onChange={(e) => handleNestedInputChange('timeTracking', 'approvalRequired', e.target.checked)}
            style={{
              width: '18px',
              height: '18px',
              cursor: 'pointer'
            }}
          />
          <label style={{
            fontSize: '14px',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            cursor: 'pointer'
          }}>
            Approval Required
          </label>
        </div>
        
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
            marginBottom: '8px',
            fontFamily: 'var(--font-family)'
          }}>
            Max Hours Per Day
          </label>
          <input
            type="number"
            value={formData.timeTracking.maxHoursPerDay}
            onChange={(e) => handleNestedInputChange('timeTracking', 'maxHoursPerDay', parseInt(e.target.value) || 8)}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid var(--border-color)',
              borderRadius: '0',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'white'
            }}
            min="1"
            max="24"
          />
        </div>
      </div>
    </div>
  );

  const renderBillingRules = () => (
    <div className="project-form-section">
      <div className="section-header">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Calculator size={20} style={{ color: '#4678be' }} />
          <h3 className="section-title" style={{
            fontSize: '18px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            margin: 0,
            fontFamily: 'var(--font-family)'
          }}>
            Billing Rules
          </h3>
        </div>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
            marginBottom: '8px',
            fontFamily: 'var(--font-family)'
          }}>
            Minimum Billable Hours
          </label>
          <input
            type="number"
            value={formData.billingRules.minimumHours}
            onChange={(e) => handleNestedInputChange('billingRules', 'minimumHours', parseFloat(e.target.value) || 0.25)}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid var(--border-color)',
              borderRadius: '0',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'white'
            }}
            step="0.25"
            min="0"
          />
        </div>
        
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
            marginBottom: '8px',
            fontFamily: 'var(--font-family)'
          }}>
            Rounding Rules
          </label>
          <select
            value={formData.billingRules.roundingRules}
            onChange={(e) => handleNestedInputChange('billingRules', 'roundingRules', e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid var(--border-color)',
              borderRadius: '0',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'white'
            }}
          >
            <option value="quarter-hour">Quarter Hour (0.25)</option>
            <option value="half-hour">Half Hour (0.5)</option>
            <option value="hour">Full Hour (1.0)</option>
            <option value="no-rounding">No Rounding</option>
          </select>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            checked={formData.billingRules.travelTime}
            onChange={(e) => handleNestedInputChange('billingRules', 'travelTime', e.target.checked)}
            style={{
              width: '18px',
              height: '18px',
              cursor: 'pointer'
            }}
          />
          <label style={{
            fontSize: '14px',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            cursor: 'pointer'
          }}>
            Bill Travel Time
          </label>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            checked={formData.billingRules.administrativeTime}
            onChange={(e) => handleNestedInputChange('billingRules', 'administrativeTime', e.target.checked)}
            style={{
              width: '18px',
              height: '18px',
              cursor: 'pointer'
            }}
          />
          <label style={{
            fontSize: '14px',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-family)',
            cursor: 'pointer'
          }}>
            Bill Administrative Time
          </label>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return renderBasicInformation();
      case 'rates':
        return renderRatesAndCosts();
      case 'tracking':
        return renderTimeTracking();
      case 'billing':
        return renderBillingRules();
      default:
        return renderBasicInformation();
    }
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Create Time & Materials Contract"
        description="Set up a new time & materials contract with hourly rates, time tracking, and cost management"
      />
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        {/* Main Content */}
        <div className="project-create-container" style={{ width: '100%', maxWidth: '100%' }}>
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            {/* Tab Navigation */}
            <div className="project-tabs">
              <div className="project-tabs-nav">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <div className="project-tab-icon">
                      {tab.icon}
                    </div>
                    <div className="project-tab-content">
                      <div className="project-tab-label">{tab.label}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="project-form-card">
              {renderTabContent()}
            </div>

            {/* Form Actions */}
            <div className="project-form-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
                {testMode && (
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={fillTestData}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      padding: '12px 24px',
                      backgroundColor: '#ffffff',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontWeight: '500',
                      fontFamily: 'var(--font-family)',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Database size={18} />
                    Fill Test Data
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => navigate('/contracts/time-materials')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 24px',
                    borderRadius: '0',
                    fontFamily: 'var(--font-family)'
                  }}
                >
                  <X size={18} />
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 24px',
                    backgroundColor: isSubmitting ? '#9ca3af' : '#4678be',
                    border: 'none',
                    color: 'white',
                    borderRadius: '0',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'var(--font-family)',
                    minWidth: '120px'
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      Create Contract
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default CreateTimeMaterialsContract;
